import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { DatabasesDisplayGridRowDataFragment } from '../../../gen-src/queries/DatabasesDisplayGridRowDataFragment';
import {
  FlowDownstreamDatabasesFilteredByFlowOutputFieldQuery,
  FlowDownstreamDatabasesFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamDatabasesConnection_nodes,
  FlowDownstreamDatabasesFilteredByFlowOutputFieldQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamDatabasesFilteredByFlowOutputFieldQuery';
import {
  FlowDownstreamDatabasesQuery,
  FlowDownstreamDatabasesQuery_flows_downstreamDatabasesConnection_nodes,
  FlowDownstreamDatabasesQueryVariables,
} from '../../../gen-src/queries/FlowDownstreamDatabasesQuery';
import { DatabaseSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  FlowDownstreamDatabasesFilteredByFlowOutputFieldQueryGql,
} from '../../queries/FlowDownstreamDatabasesFilteredByFlowOutputFieldQueryGql';
import { FlowDownstreamDatabasesQueryGql } from '../../queries/FlowDownstreamDatabasesQueryGql';
import { getRowDataWithMaxCountsForDatabase } from '../database/functions/DatabaseRowDataFunctions';
import { DatabasesDisplayGridRowData } from '../database/types/DatabasesDisplayGridRowData';
import { DatabasesDisplayGrid } from '../sharedDisplayGrid/DatabasesDisplayGrid';
import { DatabasesValidSortFields } from '../sorting/sortProviders/DatabasesGridSortProvider';

type QueryVariables = FlowDownstreamDatabasesQueryVariables;
type QueryResults = FlowDownstreamDatabasesQuery;
type QueryResultListNode = FlowDownstreamDatabasesQuery_flows_downstreamDatabasesConnection_nodes;
type GridRowData = DatabasesDisplayGridRowData;

type QueryVariablesWithFilter = FlowDownstreamDatabasesFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowDownstreamDatabasesFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter =
  FlowDownstreamDatabasesFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_downstreamDatabasesConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.downstreamDatabasesConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.downstreamDatabasesConnection : null;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow;
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field;
}

function getRowDataForDatabase(
  database: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResults,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(database, selectMaxCountsResult(resultData));
}

function getRowDataForDatabaseForFilteredQuery(
  database: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(database, selectMaxCountsResultForFilteredQuery(resultData));
}

export type FlowDownstreamDatabasesQueryGridProps =
  GridSortProps<DatabasesValidSortFields> & {
    readonly flowVizportalUrlId: string;
    readonly filteredFlowOutputFieldId: string | null;
    readonly rowCount: number | null;
  };

export function FlowDownstreamDatabasesQueryGrid(props: FlowDownstreamDatabasesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyDownstreamDatabasesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDatabasesLineageGridMessage();

  return props.filteredFlowOutputFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={FlowDownstreamDatabasesFilteredByFlowOutputFieldQueryGql}
      partialQueryVariables={{
        flowVizportalUrlId: props.flowVizportalUrlId,
        selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
        orderBy: props.orderBy as DatabaseSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDatabaseForFilteredQuery}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={FlowDownstreamDatabasesQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as DatabaseSortOrder,
          publishedConnectionsLineageEnabled
        }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDatabase}
      >
        {({ rowStore }) =>
          <DatabasesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
      </PaginatedQueryWrapper>
    );
}
